import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, LineChart, MoreHorizontal, Plus } from "lucide-react";

import { LastUpdated } from "@/components/data/last-updated";
import { RefreshButton } from "@/components/data/refresh-button";
import { SourceBadge } from "@/components/data/source-badge";
import { SyncStatusPill } from "@/components/data/sync-status-pill";
import { DeleteInvestmentDialog } from "@/components/investments/delete-investment-dialog";
import { InvestmentFormDialog } from "@/components/investments/investment-form-dialog";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { listInvestments } from "@/lib/api";
import { formatCurrency, formatPercent } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { Investment } from "@finance/shared";

interface PageState {
  status: "loading" | "ready" | "error";
  investments: Investment[];
  error: string | null;
}

const INITIAL: PageState = {
  status: "loading",
  investments: [],
  error: null,
};

const quantityFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 6,
});

function formatQuantity(value: number): string {
  return quantityFormatter.format(value);
}

export function InvestmentsPage() {
  const [state, setState] = useState<PageState>(INITIAL);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Investment | null>(null);
  const [deleting, setDeleting] = useState<Investment | null>(null);

  const load = useCallback((signal?: AbortSignal) => {
    setState((prev) =>
      prev.status === "ready"
        ? { ...prev, status: "ready", error: null }
        : INITIAL,
    );
    return listInvestments(signal)
      .then((investments) => {
        if (signal?.aborted) return;
        setState({ status: "ready", investments, error: null });
      })
      .catch((err: unknown) => {
        if (signal?.aborted) return;
        setState({
          status: "error",
          investments: [],
          error:
            err instanceof Error ? err.message : "Failed to load investments",
        });
      });
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  const rows = useMemo(() => {
    return [...state.investments].sort((a, b) =>
      a.last_updated_at < b.last_updated_at
        ? 1
        : a.last_updated_at > b.last_updated_at
          ? -1
          : 0,
    );
  }, [state.investments]);

  const totals = useMemo(() => {
    let invested = 0;
    let current = 0;
    for (const row of state.investments) {
      invested += row.cost_basis * row.quantity;
      current += row.current_value;
    }
    const pnl = current - invested;
    const pnlPct = invested > 0 ? (pnl / invested) * 100 : 0;
    return { invested, current, pnl, pnlPct };
  }, [state.investments]);

  function openAdd() {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(investment: Investment) {
    setEditing(investment);
    setFormOpen(true);
  }

  function openDelete(investment: Investment) {
    setDeleting(investment);
  }

  function afterMutation() {
    void load();
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Investments"
        title="Investments"
        description="Stocks, ETFs, and funds tracked manually with cost basis and current price. Live price feeds are a future enhancement."
        actions={
          <Button onClick={openAdd}>
            <Plus className="h-4 w-4" aria-hidden />
            Add holding
          </Button>
        }
      />

      {state.status === "ready" && rows.length > 0 ? (
        <TotalsBar
          invested={totals.invested}
          current={totals.current}
          pnl={totals.pnl}
          pnlPct={totals.pnlPct}
        />
      ) : null}

      {state.status === "loading" ? (
        <InvestmentsSkeleton />
      ) : state.status === "error" ? (
        <ErrorCard message={state.error ?? "Failed to load investments"} />
      ) : rows.length === 0 ? (
        <EmptyState onAdd={openAdd} />
      ) : (
        <InvestmentsTable
          rows={rows}
          onEdit={openEdit}
          onDelete={openDelete}
          onRefreshed={afterMutation}
        />
      )}

      <InvestmentFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        investment={editing}
        onSaved={afterMutation}
      />
      <DeleteInvestmentDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        investment={deleting}
        onDeleted={afterMutation}
      />
    </div>
  );
}

interface TotalsBarProps {
  invested: number;
  current: number;
  pnl: number;
  pnlPct: number;
}

function TotalsBar({ invested, current, pnl, pnlPct }: TotalsBarProps) {
  return (
    <Card>
      <CardContent className="grid grid-cols-2 gap-x-6 gap-y-4 py-4 sm:grid-cols-4">
        <TotalCell label="Total invested" value={formatCurrency(invested)} />
        <TotalCell label="Current value" value={formatCurrency(current)} />
        <TotalCell
          label="Unrealized P/L"
          value={`${pnl >= 0 ? "+" : "−"}${formatCurrency(Math.abs(pnl))}`}
          tone={pnlToneFromAmount(pnl)}
        />
        <TotalCell
          label="P/L %"
          value={`${pnlPct >= 0 ? "+" : "−"}${formatPercent(Math.abs(pnlPct), 2)}`}
          tone={pnlToneFromAmount(pnl)}
        />
      </CardContent>
    </Card>
  );
}

interface TotalCellProps {
  label: string;
  value: string;
  tone?: "positive" | "negative" | "neutral";
}

function TotalCell({ label, value, tone = "neutral" }: TotalCellProps) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p
        className={cn(
          "mt-1 font-mono text-lg font-semibold tabular-nums",
          tone === "positive" && "text-success",
          tone === "negative" && "text-destructive",
          tone === "neutral" && "text-foreground",
        )}
      >
        {value}
      </p>
    </div>
  );
}

type PnlTone = "positive" | "negative" | "neutral";

function pnlToneFromAmount(amount: number): PnlTone {
  if (amount > 0) return "positive";
  if (amount < 0) return "negative";
  return "neutral";
}

function formatSignedCurrency(value: number): string {
  if (value === 0) return formatCurrency(0);
  const sign = value > 0 ? "+" : "−";
  return `${sign}${formatCurrency(Math.abs(value))}`;
}

function formatSignedPercent(value: number): string {
  if (value === 0) return formatPercent(0, 2);
  const sign = value > 0 ? "+" : "−";
  return `${sign}${formatPercent(Math.abs(value), 2)}`;
}

interface InvestmentsTableProps {
  rows: Investment[];
  onEdit: (investment: Investment) => void;
  onDelete: (investment: Investment) => void;
  onRefreshed: () => void;
}

function InvestmentsTable({
  rows,
  onEdit,
  onDelete,
  onRefreshed,
}: InvestmentsTableProps) {
  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Ticker</TableHead>
            <TableHead className="text-right">Quantity</TableHead>
            <TableHead className="text-right">Cost basis</TableHead>
            <TableHead className="text-right">Current price</TableHead>
            <TableHead className="text-right">Current value</TableHead>
            <TableHead className="text-right">P/L</TableHead>
            <TableHead className="text-right">P/L %</TableHead>
            <TableHead>Source</TableHead>
            <TableHead>Sync</TableHead>
            <TableHead>Last updated</TableHead>
            <TableHead className="w-20" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const tone = pnlToneFromAmount(row.pnl_amount);
            return (
              <TableRow key={row.id}>
                <TableCell className="font-medium text-foreground">
                  {row.name}
                </TableCell>
                <TableCell className="font-mono text-muted-foreground">
                  {row.ticker ?? "—"}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums text-muted-foreground">
                  {formatQuantity(row.quantity)}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums text-muted-foreground">
                  {formatCurrency(row.cost_basis)}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums text-muted-foreground">
                  {formatCurrency(row.current_price)}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {formatCurrency(row.current_value)}
                </TableCell>
                <TableCell
                  className={cn(
                    "text-right font-mono tabular-nums",
                    tone === "positive" && "text-success",
                    tone === "negative" && "text-destructive",
                    tone === "neutral" && "text-muted-foreground",
                  )}
                >
                  {formatSignedCurrency(row.pnl_amount)}
                </TableCell>
                <TableCell
                  className={cn(
                    "text-right font-mono tabular-nums",
                    tone === "positive" && "text-success",
                    tone === "negative" && "text-destructive",
                    tone === "neutral" && "text-muted-foreground",
                  )}
                >
                  {formatSignedPercent(row.pnl_pct)}
                </TableCell>
                <TableCell>
                  <SourceBadge source={row.source} />
                </TableCell>
                <TableCell>
                  <SyncStatusPill
                    source={row.source}
                    lastSyncedAt={row.last_synced_at}
                    lastSyncStatus={row.last_sync_status}
                  />
                </TableCell>
                <TableCell>
                  <LastUpdated iso={row.last_updated_at} />
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {row.source === "api" ? (
                      <RefreshButton
                        resource="investments"
                        id={row.id}
                        label={row.name}
                        onRefreshed={onRefreshed}
                      />
                    ) : null}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Actions for ${row.name}`}
                        >
                          <MoreHorizontal className="h-4 w-4" aria-hidden />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => onEdit(row)}>
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={() => onDelete(row)}
                          className="text-destructive focus:text-destructive"
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}

function InvestmentsSkeleton() {
  return (
    <Card>
      <CardContent className="space-y-3 p-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className={cn(
              "h-10 rounded-md bg-muted/60 animate-pulse",
              i === 3 && "w-2/3",
            )}
          />
        ))}
      </CardContent>
    </Card>
  );
}

interface EmptyStateProps {
  onAdd: () => void;
}

function EmptyState({ onAdd }: EmptyStateProps) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
        <div className="grid h-10 w-10 place-items-center rounded-md border border-border bg-muted text-muted-foreground">
          <LineChart className="h-5 w-5" aria-hidden />
        </div>
        <p className="text-sm font-medium text-foreground">
          No holdings yet.
        </p>
        <p className="max-w-md text-sm text-muted-foreground">
          Add a stock, ETF, or fund to track cost basis, current price, and
          unrealized P/L.
        </p>
        <Button onClick={onAdd} className="mt-2">
          <Plus className="h-4 w-4" aria-hidden />
          Add holding
        </Button>
      </CardContent>
    </Card>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <Card className="border-destructive/40">
      <CardContent className="flex items-start gap-3 py-6">
        <AlertTriangle
          className="mt-0.5 h-5 w-5 text-destructive"
          aria-hidden
        />
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            Couldn't load investments
          </p>
          <p className="font-mono text-xs text-muted-foreground">{message}</p>
        </div>
      </CardContent>
    </Card>
  );
}
